<script setup lang="ts">
import { PhSpeakerHigh, PhSpeakerSimpleSlash } from '@phosphor-icons/vue'
import type { Props } from './type'

defineOptions({
  name: 'DistributeMediaSoundToggle',
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<{
  (event: 'toggle'): void
}>()

let lastPointerToggleAt = 0

function handlePointerDown() {
  if (props.loading) {
    return
  }

  lastPointerToggleAt = Date.now()
  emit('toggle')
}

function handleClick() {
  if (props.loading) {
    return
  }

  if (Date.now() - lastPointerToggleAt < 700) {
    return
  }

  emit('toggle')
}
</script>

<template>
  <button
    type="button"
    class="media-sound-toggle"
    :class="{ 'is-loading': loading }"
    :aria-label="loading ? '视频加载中' : muted ? '打开声音' : '关闭声音'"
    :aria-disabled="loading"
    :aria-pressed="!muted"
    @pointerdown.stop.prevent="handlePointerDown"
    @mousedown.stop.prevent
    @click.stop="handleClick"
  >
    <span v-if="loading" class="media-sound-toggle__spinner" aria-hidden="true" />
    <PhSpeakerSimpleSlash v-else-if="muted" :size="24" weight="fill" />
    <PhSpeakerHigh v-else :size="24" weight="fill" />
  </button>
</template>

<style scoped>
.media-sound-toggle {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 4;
  display: grid;
  width: 24px;
  height: 24px;
  padding: 0;
  place-items: center;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.2));
  outline: none;
  opacity: 0;
  pointer-events: none;
  transition:
    color 0.16s ease,
    filter 0.16s ease,
    opacity 0.16s ease,
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.media-sound-toggle.is-loading {
  cursor: progress;
}

.media-sound-toggle:focus-visible {
  opacity: 1;
  pointer-events: auto;
}

.media-sound-toggle:hover,
.media-sound-toggle:focus-visible {
  color: rgba(255, 255, 255, 0.9);
  filter: drop-shadow(0 2px 10px rgba(0, 0, 0, 0.26));
  transform: scale(var(--app-interaction-object-scale-hover));
}

.media-sound-toggle:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.media-sound-toggle svg {
  display: block;
}

.media-sound-toggle__spinner {
  display: block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.32);
  border-top-color: rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  animation: media-sound-toggle-spin 0.8s linear infinite;
  transform-origin: 50% 50%;
  will-change: transform;
}

@keyframes media-sound-toggle-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .media-sound-toggle {
    transition:
      color 0.16s ease,
      filter 0.16s ease;
  }

  .media-sound-toggle:hover,
  .media-sound-toggle:focus-visible,
  .media-sound-toggle:active {
    transform: none;
  }

  .media-sound-toggle__spinner {
    animation: none;
  }
}
</style>
